<template>
  <layout-auth>
    <validation-observer ref="form" v-slot="{ passes }">
      <auth-form-base :message="errorMessage">
        <v-form @submit.prevent="passes(submit)" v-if="!isTwoFA">
          <ValidationProvider v-slot="{ errors }" name="Email" rules="required">
            <v-text-field
              prepend-icon="mdi-account"
              v-model.trim="email"
              label="Login"
              autocapitalize="off"
              data-test-login="login"
              :error-messages="errors"
              type="text"
            ></v-text-field>
          </ValidationProvider>

          <ValidationProvider
            v-slot="{ errors }"
            name="Password"
            rules="required"
          >
            <v-text-field
              prepend-icon="mdi-lock"
              v-model.trim="password"
              data-test="password"
              label="Password"
              :error-messages="errors"
              type="password"
            ></v-text-field>
          </ValidationProvider>
          <v-btn
            class="mt-4 mb-3"
            block
            type="submit"
            color="primary"
            data-test="actionLogin"
            depressed
            :disabled="isLoading"
            :loading="isLoading"
          >
            Login
          </v-btn>
        </v-form>
        <v-form @submit.prevent="passes(loginWithOtp)" v-else>
          <v-otp-input
            :title="twoFaInfo"
            autofocus
            ref="otpInput"
            length="6"
            v-model="otp"
            type="number"
            @finish="isOTPDisabled = false"
          ></v-otp-input>

          <div class="subtitle-1">
            {{ twoFaHint }}
          </div>
          <v-btn
            class="mt-4 mb-3"
            block
            outlined
            type="click"
            @click="submit"
            color="primary"
            data-test="actionResendOtp"
            depressed
            :loading="isLoading"
          >
            Resend
          </v-btn>
          <v-btn
            class="mt-4 mb-3"
            block
            type="submit"
            color="primary"
            data-test="actionLogin"
            depressed
            :disabled="isOTPDisabled || otp.length < 6"
            :loading="isLoginViaOtp"
          >
            Continue
          </v-btn>
        </v-form>
      </auth-form-base>
    </validation-observer>
  </layout-auth>
</template>

<script>
import LayoutAuth from './AuthLayout';
import AuthFormBase from './AuthFormBase';
import { extend, ValidationObserver, ValidationProvider } from 'vee-validate';

import useAuth from '@/api/auth';
import { ref } from '@vue/composition-api';
import { loadLanguageAsync } from '@/i18n-setup';
import { required } from 'vee-validate/dist/rules';
import useErrors from '@/api/errors';

extend('required', {
  ...required,
  message: 'This field is required'
});

export default {
  name: 'AuthFormLogin',
  components: {
    LayoutAuth,
    AuthFormBase,
    ValidationObserver,
    ValidationProvider
  },
  setup(props, context) {
    const email = ref('');
    const password = ref('');
    const otp = ref('');
    const errorMessage = ref('');
    const twoFaHint = ref('');
    const twoFaInfo = ref('');
    const isLoading = ref(false);
    const isLoginViaOtp = ref(false);
    const isTwoFA = ref(false);
    const isOTPDisabled = ref(true);
    const { login, submitOTP } = useAuth(context);
    const form = ref(null);
    const { error } = useErrors();

    const loginWithOtp = async () => {
      if (!isLoading.value) {
        errorMessage.value = '';
        isLoginViaOtp.value = true;
        try {
          await submitOTP(otp.value);
          await loadLanguageAsync();
          const last = localStorage.getItem('lastUrlPathname');
          localStorage.removeItem('lastUrlPathname');
          errorMessage.value = '';
          error.value = null;
          if (last && !last.includes('login')) {
            context.root.$router.push(last);
          } else {
            context.root.$router.push({ name: 'devices' });
          }
        } catch (e) {
          errorMessage.value = e.message;
        } finally {
          isLoginViaOtp.value = false;
        }
      }
    };

    const submit = async () => {
      if (!isLoading.value) {
        errorMessage.value = '';
        isLoading.value = true;
        try {
          const loginData = await login({
            userLogin: email.value,
            userPassword: password.value
          });

          if (loginData?.twoFA) {
            twoFaHint.value = `OTP-code has been sent by ${loginData?.twoFA.type}`;
            twoFaInfo.value = `${loginData?.twoFA.info}-XXXXXX`;
            isTwoFA.value = true;
          } else {
            await loadLanguageAsync();
            const last = localStorage.getItem('lastUrlPathname');
            localStorage.removeItem('lastUrlPathname');
            errorMessage.value = '';
            error.value = null;
            if (last && !last.includes('login')) {
              context.root.$router.push(last);
            } else {
              context.root.$router.push({ name: 'devices' });
            }
          }
        } catch (e) {
          errorMessage.value = e.message;
        } finally {
          isLoading.value = false;
        }
      }
    };

    return {
      email,
      password,
      errorMessage,
      submit,
      isLoading,
      form,
      loginWithOtp,
      isOTPDisabled,
      isTwoFA,
      twoFaHint,
      twoFaInfo,
      otp,
      isLoginViaOtp
    };
  }
};
</script>
